import { useState, useEffect } from 'react';
import { ShiftAlert } from '../services/api';

// Hardcoded shift alerts data
const hardcodedShiftAlerts: ShiftAlert[] = [
  {
    id: 1,
    facility: 'Sunrise Medical Center',
    role: 'RN',
    payRate: 38,
    shiftTime: '7:00 AM - 7:00 PM',
    location: 'Cleveland, OH',
    urgency: 'urgent',
    initials: 'SM',
  },
  {
    id: 2,
    facility: 'Green Valley Hospital',
    role: 'LPN',
    payRate: 32,
    shiftTime: '7:00 PM - 7:00 AM',
    location: 'Akron, OH',
    urgency: 'normal',
    initials: 'GV',
  },
  {
    id: 3,
    facility: 'Riverbend Clinic',
    role: 'CNA',
    payRate: 28,
    shiftTime: '3:00 PM - 11:00 PM',
    location: 'Columbus, OH',
    urgency: 'urgent',
    initials: 'RC',
  },
  {
    id: 4,
    facility: 'Lakeside Health',
    role: 'RN',
    payRate: 40,
    shiftTime: '11:00 PM - 7:00 AM',
    location: 'Toledo, OH',
    urgency: 'normal',
    initials: 'LH',
  },
  {
    id: 5,
    facility: 'Maplewood Care',
    role: 'LPN',
    payRate: 34,
    shiftTime: '7:00 AM - 3:00 PM',
    location: 'Dayton, OH',
    urgency: 'normal',
    initials: 'MC',
  },
  {
    id: 6,
    facility: 'Oakridge Hospital',
    role: 'CNA',
    payRate: 30,
    shiftTime: '3:00 PM - 11:00 PM',
    location: 'Cincinnati, OH',
    urgency: 'urgent',
    initials: 'OH',
  },
];

const ShiftAlerts = () => {
  const [shiftAlerts, setShiftAlerts] = useState<ShiftAlert[]>([]);
  const [filteredAlerts, setFilteredAlerts] = useState<ShiftAlert[]>([]);
  const [selectedRole, setSelectedRole] = useState('All');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const roles = ['All', 'RN', 'LPN', 'CNA'];

  useEffect(() => {
    // Use hardcoded data instead of API
    setLoading(true);
    setError(null);
    setShiftAlerts(hardcodedShiftAlerts);
    setLoading(false);
  }, []);

  useEffect(() => {
    filterAlertsByRole();
  }, [shiftAlerts, selectedRole]);

  const filterAlertsByRole = () => {
    if (selectedRole === 'All') {
      setFilteredAlerts(shiftAlerts);
    } else {
      const filtered = shiftAlerts.filter(alert => {
        const alertRole = alert.role.split(' - ')[1] || alert.role.split(' - ')[0];
        return alertRole === selectedRole;
      });
      setFilteredAlerts(filtered);
    }
  };

  const handleRoleFilter = (role: string) => {
    setSelectedRole(role);
  };

  const handleApplyNow = (alertId: number) => {
    // Handle apply functionality
    console.log('Applying for shift:', alertId);
    alert('Application submitted! We will contact you soon.');
  };

  if (loading) {
    return (
      <section className="py-20 bg-gradient-to-br from-card1 to-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex justify-center items-center py-16">
            <div className="text-center">
              <div className="w-16 h-16 border-4 border-secondary border-t-transparent rounded-full animate-spin mx-auto mb-6"></div>
              <p className="text-xl text-primaryLight font-semibold">Loading shift alerts...</p>
            </div>
          </div>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="py-20 bg-gradient-to-br from-card1 to-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center py-16">
            <div className="w-20 h-20 bg-red100 rounded-full flex items-center justify-center mx-auto mb-6">
              <i className="fas fa-exclamation-triangle text-3xl text-red500"></i>
            </div>
            <p className="text-xl text-red500 mb-6 font-semibold">{error}</p>
            <button 
              onClick={() => {
                setLoading(true);
                setError(null);
                setShiftAlerts(hardcodedShiftAlerts);
                setLoading(false);
              }}
              className="bg-gradient-to-r from-secondary to-secondaryDark text-primary font-bold px-8 py-3 rounded-2xl shadow-soft hover:shadow-medium transition-all duration-300 transform hover:scale-105"
            >
              Try Again
            </button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-20 bg-gradient-to-br from-blue100 via-card1 to-white relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute top-0 left-0 w-full h-full">
        <div className="absolute top-20 right-10 w-32 h-32 bg-blue500/5 rounded-full animate-float"></div>
        <div className="absolute bottom-20 left-10 w-24 h-24 bg-green600/5 rounded-full animate-float" style={{ animationDelay: '2s' }}></div>
      </div>
      
      <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16 animate-fade-in">
          <div className="flex items-center justify-center gap-4 mb-6">
            <div className="w-16 h-16 bg-gradient-to-br from-blue500 to-blue700 rounded-2xl flex items-center justify-center">
              <i className="fas fa-bell text-2xl text-white"></i>
            </div>
            <div>
              <h2 className="text-4xl md:text-5xl font-bold text-primary">Shift Alerts</h2>
              <div className="flex items-center gap-3 mt-2">
                <span className="bg-badgePrimary text-white px-3 py-1 rounded-full text-sm font-bold animate-pulse-slow">
                  LIVE
                </span>
                <span className="text-sm text-primaryLight font-medium">Real-time updates</span>
              </div>
            </div>
          </div>
          <p className="text-xl text-primaryLight max-w-3xl mx-auto leading-relaxed">
            Real-time notifications for available healthcare shifts. Find your perfect opportunity with instant updates and competitive pay rates.
          </p>
        </div>

        {/* Role Filters */}
        <div className="flex flex-wrap justify-center gap-4 mb-12">
          {roles.map((role, index) => (
            <button
              key={role}
              onClick={() => handleRoleFilter(role)}
              className={`px-8 py-4 rounded-2xl font-bold text-lg transition-all duration-300 transform hover:scale-105 ${
                selectedRole === role
                  ? 'bg-gradient-to-r from-primary to-primaryDark text-white shadow-strong'
                  : 'bg-white text-primary border-2 border-iconFourth hover:border-blue500 hover:shadow-soft'
              }`}
              style={{ animationDelay: `${index * 100}ms` }}
            >
              {role}
            </button>
          ))}
        </div>

        {/* Shift Alert Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
          {filteredAlerts.map((alert, index) => (
            <div
              key={alert.id}
              className={`bg-card-gradient rounded-3xl shadow-soft hover:shadow-strong transition-all duration-500 transform hover:-translate-y-2 overflow-hidden ${
                alert.urgency === 'urgent' ? 'ring-2 ring-red500/20 bg-gradient-to-br from-red100/50 to-white' : ''
              }`}
              style={{ animationDelay: `${index * 150}ms` }}
            >
              {/* Header */}
              <div className="p-6 border-b border-iconFourth/20">
                <div className="flex items-start justify-between mb-4">
                  <div className="w-14 h-14 bg-gradient-to-br from-blue500 to-blue700 rounded-2xl flex items-center justify-center font-bold text-xl text-white shadow-soft">
                    {alert.initials}
                  </div>
                  {alert.urgency === 'urgent' && (
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 bg-red500 rounded-full animate-pulse-slow"></div>
                      <span className="bg-red500 text-white px-3 py-1 rounded-full text-xs font-bold">
                        URGENT
                      </span>
                    </div>
                  )}
                </div>

                {/* Role */}
                <h3 className="text-xl font-bold text-primary mb-2">{alert.role}</h3>

                {/* Facility */}
                <p className="text-primaryLight font-medium mb-3 flex items-center gap-2">
                  <i className="fas fa-hospital text-blue500"></i>
                  {alert.location}
                </p>

                {/* Shift Time */}
                <p className="text-sm text-primaryLight mb-4 flex items-center gap-2">
                  <i className="fas fa-clock text-orange500"></i>
                  {alert.shiftTime}
                </p>
              </div>

              {/* Pay Rate */}
              <div className="px-6 py-4 bg-gradient-to-r from-green100/50 to-green100/20">
                <div className="flex items-center justify-between">
                  <span className="text-3xl font-bold text-green600">${alert.payRate}/hr</span>
                  <div className="text-sm text-green800 font-semibold bg-green100 px-3 py-1 rounded-full">
                    Competitive Rate
                  </div>
                </div>
              </div>

              {/* Apply Button */}
              <div className="p-6">
                <button
                  onClick={() => handleApplyNow(alert.id)}
                  className="w-full bg-gradient-to-r from-primary to-primaryDark text-white py-4 px-6 rounded-2xl font-bold shadow-soft hover:shadow-strong transition-all duration-300 transform hover:scale-105 flex items-center justify-center gap-3"
                >
                  <i className="fas fa-paper-plane"></i>
                  Apply Now
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* No Results */}
        {filteredAlerts.length === 0 && (
          <div className="text-center py-16">
            <div className="w-20 h-20 bg-iconFourth rounded-full flex items-center justify-center mx-auto mb-6">
              <i className="fas fa-search text-3xl text-primaryLight"></i>
            </div>
            <h3 className="text-2xl font-bold text-primary mb-4">No shifts available</h3>
            <p className="text-lg text-primaryLight mb-8">
              No shift alerts available for <span className="font-semibold text-primary">{selectedRole}</span> role at the moment.
            </p>
            <button 
              onClick={() => setSelectedRole('All')}
              className="bg-gradient-to-r from-secondary to-secondaryDark text-primary font-bold px-8 py-3 rounded-2xl shadow-soft hover:shadow-medium transition-all duration-300 transform hover:scale-105"
            >
              View All Shifts
            </button>
          </div>
        )}

        {/* Quick Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-16">
          <div className="bg-white rounded-2xl p-6 shadow-soft text-center">
            <div className="w-12 h-12 bg-blue100 rounded-xl flex items-center justify-center mx-auto mb-4">
              <i className="fas fa-users text-blue600 text-xl"></i>
            </div>
            <div className="text-2xl font-bold text-primary mb-2">{shiftAlerts.length}</div>
            <div className="text-primaryLight font-semibold">Total Shifts</div>
          </div>
          <div className="bg-white rounded-2xl p-6 shadow-soft text-center">
            <div className="w-12 h-12 bg-green100 rounded-xl flex items-center justify-center mx-auto mb-4">
              <i className="fas fa-star text-green600 text-xl"></i>
            </div>
            <div className="text-2xl font-bold text-primary mb-2">
              ${Math.round(shiftAlerts.reduce((sum, alert) => sum + alert.payRate, 0) / shiftAlerts.length)}
            </div>
            <div className="text-primaryLight font-semibold">Avg Pay Rate</div>
          </div>
          <div className="bg-white rounded-2xl p-6 shadow-soft text-center">
            <div className="w-12 h-12 bg-orange100 rounded-xl flex items-center justify-center mx-auto mb-4">
              <i className="fas fa-bolt text-orange600 text-xl"></i>
            </div>
            <div className="text-2xl font-bold text-primary mb-2">2.3 min</div>
            <div className="text-primaryLight font-semibold">Avg Response Time</div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ShiftAlerts; 